import _ from "lodash";
import React from "react";
import { updateTemplate } from "IBPlanner/modules/IBPlannerModule";
import {
  getRethumbUrl,
  generateRandomId,
  getYearDurationFormat,
  getArchivedSortedSubjectListMemoized,
  getMemoizedLangFunction,
  getMemoizedFunction,
} from "Utils";
import {
  CURRICULUM_CONFIGURATION,
  getCurriculumOverviewWithCustomDatasets,
} from "PlanningInsights/modules/Module";
import { getCourseDetailsFromCache } from "Course/modules/CourseGraphqlHelpers";
import {
  CURRICULUM_TYPE_MYP,
  CURRICULUM_TYPE_UBD,
  NODE_TYPE_IB_PYP_CURRICULUM_PROGRAM,
  ELEMENT_TYPE_PYP_THEME,
  MYP_SUBJECT_SPECIFIC,
  MYP_INTERDISCIPLINARY,
} from "Constants/stringConstants";
import { getAcademicYearDetailsFromCahce } from "modules/CommonGraphqlHelpers";

import update from "immutability-helper";
import { PLANNER_ELEMENT_LABEL_MAPPING, colors } from "Constants";
import ACLStore from "lib/aclStore";
import classNames from "classnames";
import { Tooltip } from "@toddle-edu/ds-web";
import { InformationOutlined } from "@toddle-edu/ds-icons";
import {
  SHOW_CUSTOM_DATASET_INSIGHTS,
  SHOW_SINGLE_TABLE_INSIGHTS,
} from "Constants/permissionConstants";
import { getCurrentAcademicYear } from "modules/CommonSelectors";

export const getIsCustomDatasetInsightsEnabled = () => {
  return ACLStore.can(SHOW_CUSTOM_DATASET_INSIGHTS);
};

export const isSingleTableInsightsEnabled = () => {
  return ACLStore.can(SHOW_SINGLE_TABLE_INSIGHTS);
};

const checkNodeExistsForGrades = ({ node, grades = [] }) => {
  const nodeGrades = _.get(node, "grades", []);
  return _.get(_.intersectionBy(nodeGrades, grades, "id"), "length", 0) > 0;
};

const generateTreeOfNode = ({ nodeId, nodes, grades = [] }) => {
  const node = _.find(nodes, { id: nodeId });
  const outputNodes = [];

  if (grades.length > 0 && !checkNodeExistsForGrades({ node, grades })) {
    return outputNodes;
  }

  _.forEach(_.get(node, "children", []), childId => {
    const childNode = _.find(nodes, { id: childId });

    if (_.isEmpty(childNode)) {
      return;
    }

    if (
      grades.length > 0 &&
      !checkNodeExistsForGrades({ node: childNode, grades })
    ) {
      return;
    }

    const children = generateTreeOfNode({ nodeId: childId, nodes, grades });

    outputNodes.push({ ...childNode, children });
  });

  return outputNodes;
};

const getElementsNestedData = ({
  nodes,
  elementType,
  selectedYearId,
  parentNodes,
}) => {
  const rootNodes = _.filter(nodes, node => _.isEmpty(node.parent));

  switch (elementType) {
    case "PYP_KEY_CONCEPT":
    case "PYP_LEARNER_PROFILE":
    case "MYP_GLOBAL_CONTEXT":
    case "MYP_KEY_CONCEPT":
    case "MYP_LEARNER_PROFILE":
    case "MYP_COMMUNITY_ENGAGEMENT":
    case "MYP_LEARNING_OBJECTIVES":
      return _.map(rootNodes, node => {
        return _.find(nodes, { id: node.id });
      });
    case "PYP_LEARNING_STANDARD": {
      return _.map(parentNodes, parentNode => {
        return {
          ..._.find(nodes, node => node.id == parentNode),
          children: generateTreeOfNode({
            nodes,
            nodeId: parentNode,
          }),
          parent: null,
        };
      });
    }
    case "PYP_ATL":
    case "MYP_SERVICE_ACTION_OUTCOME":
    case "MYP_LEARNING_STANDARD":
    case "MYP_ATL":
    case "MYP_SPECIFIED_CONCEPTS":
    case "MYP_RELATED_CONCEPT":
    case "UBD_LEARNING_STANDARD":
    case "UBD_ATL":
    case "DP_SUBJECT_STANDARD":
    case "DP_AIM":
    case "DP_OBJECTIVES":
    case "DP_TOK_CONNECTION":
    case "DP_ATL":
    case "NESTED_CHECKLIST":
      return _.map(rootNodes, node => {
        return {
          ..._.find(nodes, { id: node.id }),
          children: generateTreeOfNode({
            nodes,
            nodeId: node.id,
          }),
        };
      });
    case "MYP_OBJECTIVES": {
      const nestedNodes = _.map(rootNodes, node => {
        return {
          ..._.find(nodes, { id: node.id }),
          children: generateTreeOfNode({
            nodes,
            nodeId: node.id,
          }),
        };
      });

      return nestedNodes;
    }
  }
};

export const getLocalisedTemplateFieldObjectsMemoize = getMemoizedLangFunction(
  ({ templateFieldObject, t }) => {
    return _.reduce(
      templateFieldObject,
      (result, obj, key) => {
        result[key] = {
          ...obj,
          config: {
            ...obj.config,
            viewLabel: t(obj.config.viewLabelLocale),
          },
        };
        return result;
      },
      {}
    );
  }
);

export const getElementsNestedDataMemoized = _.memoize(
  params => getElementsNestedData(params),
  params => JSON.stringify(params)
);

const flattenTreeStructure = ({ nodes }) => {
  const data = _.cloneDeep(nodes);

  return _.reduce(
    data,
    function (result, next) {
      if (_.isObject(next)) {
        result.push(next);

        if (next.children) {
          result = _.concat(
            result,
            flattenTreeStructure({ nodes: next.children })
          );

          next.children = _.map(
            next.children,
            child => !_.isEmpty(child) && child.id
          );
        }
      }
      return result;
    },
    []
  );
};

export const flattenTreeStructureMemoized = _.memoize(
  params => flattenTreeStructure(params),
  params => JSON.stringify(params)
);

/**
 * Sorts nodes in a hierarchical structure, starting from specified depth.
 * Each node is followed by its children in the same order as in the children array.
 *
 * @param {Object} params - The parameters object
 * @param {Array} params.nodes - Array of node objects with id, parent, and children properties
 * @param {number} [params.startingDepth=1] - The depth to start sorting from (default is 1)
 * @returns {Array} Sorted array of nodes in hierarchical order
 */
export const sortFlatNodes = ({ nodes, startingDepth = 1 }) => {
  // Create a map of node id to node for quick lookup
  const nodeMap = _.keyBy(nodes, "id");

  // Get nodes at the specified starting depth
  const startingNodes = _.filter(nodes, node => node.depth === startingDepth);

  // Result array to store sorted nodes
  const result = [];

  // Recursive function to traverse the tree, preserving children order
  const traverseNode = node => {
    if (!node) return;

    // Add current node to result
    result.push(node);

    // Process all children (if any) in their original order
    if (node.children && node.children.length) {
      _.forEach(node.children, childId => {
        const childNode = nodeMap[childId];
        if (childNode) {
          traverseNode(childNode);
        }
      });
    }
  };

  // Start traversal from each starting node in their original order
  _.forEach(startingNodes, node => {
    traverseNode(node);
  });

  return result;
};

const getFiltersByRoute = ({
  route,
  upToDepth,
  subjectIds,
  selectedSubjectId,
  parentNodes,
  elementType,
  elementTypes,
  overviewType,
  unitTypeFilter,
  academicYearId,
  unitTypeOptions,
  gradeIds,
  selectedUnitType,
  curriculumProgramId,
  academicYear,
  isSingleTableInsights,
}) => {
  switch (route) {
    case "sgo-timeline":
      return {
        subjects: subjectIds,
        grades: gradeIds,
        academicYears: [academicYearId],
        unitTypes:
          _.isEmpty(selectedUnitType) || selectedUnitType == "ALL"
            ? []
            : [selectedUnitType],
      };
    case "sgo":
      return {
        subjects: subjectIds,
        academicYears: [academicYearId],
        grades: gradeIds ?? [],
      };

    case "poi":
      switch (overviewType) {
        case "PROGRAMME_OVERVIEW":
          return {
            id: curriculumProgramId,
            type: NODE_TYPE_IB_PYP_CURRICULUM_PROGRAM,
            elementTypes: [ELEMENT_TYPE_PYP_THEME],
            themeFilters: { academicYear },
          };
        case "VERTICAL_OVERVIEW":
          return {
            elementTypes,
            subjects: subjectIds,
            academicYears: [academicYearId],
          };
        default:
          return {};
      }

    case "otherInsights":
      return {
        elementTypes,
        subjects: subjectIds,
        academicYears: [academicYearId],
      };
    case "skills":
      switch (overviewType) {
        case "HORIZONTAL_OVERVIEW":
          return {
            subjects: subjectIds,
            elementTypes: [elementType],
            academicYears: [academicYearId],
          };
        case "VERTICAL_OVERVIEW":
          return {};
        default:
          return {};
      }
    case "globalContexts": {
      switch (overviewType) {
        case "HORIZONTAL_OVERVIEW":
          return {
            subjects: subjectIds,
            elementTypes: [elementType],
            academicYears: [academicYearId],
          };
        case "VERTICAL_OVERVIEW":
          return {};
        default:
          return {};
      }
    }
    case "lpas":
      return {};
    case "krc": {
      switch (elementType) {
        case "MYP_KEY_CONCEPT":
          return {};
        case "MYP_RELATED_CONCEPT":
          return {
            associatedParents: { ids: subjectIds, type: "SUBJECT" },
          };
        default:
          return {};
      }
    }
    case "objectives": {
      const associatedParents = { ids: subjectIds, type: "SUBJECT" };

      if (!_.isUndefined(upToDepth)) {
        // To get all root nodes of objectives
        return {
          associatedParents,
          upToDepth,
        };
      } else {
        // To get all nodes for particular nodes of objectives
        return {
          associatedParents,
          parentNodes,
        };
      }
    }
    case "keyConcepts": {
      return {
        subjects: subjectIds,
        elementTypes: [elementType],
        academicYears: [academicYearId],
      };
    }
    case "units": {
      return {
        subjects: [subjectIds],
        //changing this for v2 paginated query, unitTypes was supported in older version
        unitType: _.isEqual(unitTypeFilter, "ALL") ? [] : [unitTypeFilter],
        academicYears: [academicYearId],
        isIncludedInInsights: true,
        grades: gradeIds,
      };
    }
    case "standards": {
      switch (elementType) {
        case "PYP_LEARNING_STANDARD":
          return {
            subjects: subjectIds,
            ...(isSingleTableInsights ? { grades: gradeIds } : {}),
          };
        case "MYP_LEARNING_STANDARD": {
          const associatedParents = [];

          if (isSingleTableInsights) {
            if (!_.isEmpty(gradeIds)) {
              associatedParents.push({
                ids: gradeIds,
                type: "GRADE",
              });
            }

            associatedParents.push({
              ids: [selectedSubjectId],
              type: "SUBJECT",
            });
          } else {
            associatedParents.push({
              ids: subjectIds,
              type: "SUBJECT",
            });
          }

          return {
            subjectPOIFilters: {
              elementTypes: [elementType],
              subjects: isSingleTableInsights
                ? [selectedSubjectId]
                : subjectIds,
              academicYears: [academicYearId],
            },
            plannerElementSetFilters: {
              associatedParents,
            },
          };
        }
        case "UBD_LEARNING_STANDARD": {
          const associatedParents = [
            {
              ids: subjectIds,
              type: "SUBJECT",
            },
          ];

          if (isSingleTableInsights && !_.isEmpty(gradeIds)) {
            associatedParents.push({
              ids: gradeIds,
              type: "GRADE",
            });
          }

          return {
            subjectPOIFilters: {
              elementTypes: [elementType],
              academicYears: [academicYearId],
              // TODO: this does nothing right now
              // ...(isSingleTableInsights ? { grades: gradeIds } : {}),
            },
            plannerElementSetFilters: {
              associatedParents,
            },
          };
        }

        case "DP_SUBJECT_STANDARD": {
          const associatedParents = { ids: subjectIds, type: "SUBJECT" };
          return { associatedParents };
        }
        default:
          return {};
      }
    }
    case "subject-timeline": {
      return {
        subjects: [subjectIds],
        academicYears: [academicYearId],
        unitTypes: _.isEqual(unitTypeFilter, "ALL")
          ? _.map(
              _.filter(unitTypeOptions, item => item.key != "ALL"),
              item => item.key
            )
          : [unitTypeFilter],
        isIncludedInInsights: true,
      };
    }
    case "assessmentObjectives":
    case "learning-outcomes":
    case "aims": {
      const associatedParents = { ids: subjectIds, type: "SUBJECT" };
      return { associatedParents };
    }
  }
};

export const getFiltersByRouteMemoized = _.memoize(
  params => getFiltersByRoute(params),
  params => JSON.stringify(params)
);

/**
 * This function orders the planner element nodes as per the associated
 * parent id. It puts matching parent nodes first and rest of the nodes
 * later which are at least used once.
 *
 * @param {Object} param0
 * @param {Array} param0.dataNodes - Flatten planner element nodes
 * @param {Number} param0.parentId - Associated parent id of the nodes
 * @param {Array} param0.insightData - Insight data with unit plans, total count
 * and planner nodes
 */
const getCustomOrderedNodes = ({
  dataNodes,
  parentId,
  insightData,
  sendWithSubjectGroupLabel = false,
  subjectGroupName,
  t,
}) => {
  const parentSpecificNodes = _.filter(dataNodes, node => {
    const associatedParents = _.map(node.associatedParents, parent => {
      return parent.id;
    });

    return _.includes(associatedParents, parentId);
  });

  const otherNodes = _.filter(dataNodes, node => {
    const associatedParents = _.map(
      node.associatedParents,
      parent => parent.id
    );

    return !_.includes(associatedParents, parentId);
  });

  if (sendWithSubjectGroupLabel) {
    let updatedNodes = [];

    if (!_.isEmpty(parentSpecificNodes)) {
      updatedNodes = [
        {
          showRowWithGroupLabel: true,
          rowLabel: t("common:concepts_with_subject_group_label", {
            subjectGroup: subjectGroupName,
          }),
        },
        ...parentSpecificNodes,
      ];
    }

    if (!_.isEmpty(otherNodes)) {
      updatedNodes = updatedNodes.concat(
        {
          showRowWithGroupLabel: true,
          rowLabel: t("common:other_subject_group_key_concepts_label"),
        },
        ...otherNodes
      );
    }

    return updatedNodes;
  }

  return [...parentSpecificNodes, ...otherNodes];
};

export const getCustomOrderedNodesMemoized = _.memoize(
  params => getCustomOrderedNodes(params),
  params => JSON.stringify(params)
);

export const getCustomOrderedSpecifiedConcepts = ({
  dataNodes,
  parentId,
  subjectGroupName,
  t,
}) => {
  const parentSpecificNodes = _.filter(dataNodes, node => {
    const associatedParents = _.map(node.associatedParents, parent => {
      return parent.id;
    });

    return _.includes(associatedParents, parentId);
  });

  const otherNodes = _.filter(dataNodes, node => {
    const associatedParents = _.map(
      node.associatedParents,
      parent => parent.id
    );

    return !_.includes(associatedParents, parentId);
  });

  let updatedNodes = [];

  if (!_.isEmpty(parentSpecificNodes)) {
    updatedNodes = [
      {
        showRowWithGroupLabel: true,
        rowLabel: t(
          "planningInsights:specified_concepts_with_subject_group_label",
          {
            subjectGroup: subjectGroupName,
          }
        ),
      },
      ...parentSpecificNodes,
    ];
  }

  if (!_.isEmpty(otherNodes)) {
    updatedNodes = updatedNodes.concat(
      {
        showRowWithGroupLabel: true,
        rowLabel: t(
          "planningInsights:other_subject_group_specified_concepts_label"
        ),
      },
      ...otherNodes
    );
  }

  return updatedNodes;
};

/**
 * This function orders subjects as per the selected subject group.
 * It puts matching subjects which belongs to the selected subject group
 * first and rest of the nodes later which belongs to other subject groups
 *
 * @param {Object} param0
 * @param {String} param0.subjectId - subject id of selected course
 * @param {Array} param0.dataNodes - Insight data nodes
 * @param {Array} param0.selectedSubjectIds - Subject ids which are selected in filter
 * @param {Array} param0.subjectIdsOfSelectedSG - Subject ids of selected subject group
 */

const getOrderedHeader = ({ courseSubjectList, dataNodes }) => {
  //filtering data nodes with course subject
  const courseDataNodes = _.filter(dataNodes, node => {
    return _.find(courseSubjectList, subject =>
      _.some(_.get(node, "edges", []), item => item.subject.id == subject.id)
    );
  });

  //get the course node in starting of the array
  return _.unionBy(courseDataNodes, dataNodes, "plannerNode.id");
};

const getCustomOrderedHeader = ({
  subjectId,
  subjects,
  selectedSubjectIds,
  subjectIdsOfSelectedSG,
}) => {
  const commonSubjectIds = _.intersection(
    selectedSubjectIds,
    subjectIdsOfSelectedSG
  );

  // subject of selected course
  let subjectGroupSpecificSubjects = _.filter(subjects, subject => {
    return (
      _.includes(commonSubjectIds, subject.id) &&
      !_.isEqual(subject.id, subjectId)
    );
  });

  // Set the subject of selected course first
  if (_.includes(commonSubjectIds, subjectId)) {
    const courseSubject = _.find(subjects, subject =>
      _.isEqual(subject.id, subjectId)
    );

    if (courseSubject) {
      subjectGroupSpecificSubjects = _.concat(
        [courseSubject],
        subjectGroupSpecificSubjects
      );
    }
  }

  const otherSubjects = _.filter(
    subjects,
    subject => !_.includes(subjectIdsOfSelectedSG, subject.id)
  );

  return [...subjectGroupSpecificSubjects, ...otherSubjects];
};

export const getCustomOrderedHeaderMemoized = _.memoize(
  params => getCustomOrderedHeader(params),
  params => JSON.stringify(params)
);

export const getOrderedHeaderMemoized = _.memoize(
  params => getOrderedHeader(params),
  params => JSON.stringify(params)
);

const getCommonInsightElementFromUrl = ({
  lastPath,
  overviewType,
  currentCurriculumProgram,
}) => {
  const curriculumInsights = getCurriculumOverviewWithCustomDatasets({
    currentCurriculumProgram,
  });

  const overview = _.find(curriculumInsights, {
    type: overviewType,
  });
  return _.find(overview.children, {
    route: lastPath,
  });
};

//Returns insightElement based on curriculumType
export const getCommonInsightElementFromUrlMemoized = _.memoize(
  params => getCommonInsightElementFromUrl(params),
  params => JSON.stringify(params)
);

export const getSubjectsByCourseId = ({ courseId, academicYearId }) => {
  const courseDetails = getCourseDetailsFromCache({
    id: courseId,
    courseGradeFilters: {
      academicYearId,
    },
  });
  return _.get(courseDetails, "subjects", []);
};

export const getSubjectGroupByCourseId = ({ courseId, academicYearId }) => {
  const subjects = getSubjectsByCourseId({ courseId, academicYearId });
  return _.first(
    _.reduce(
      subjects,
      (result, { subjectGroup }) => {
        if (subjectGroup) {
          result = _.unionBy(result, [subjectGroup], "id");
        }

        return result;
      },
      []
    )
  );
};

/**
 * This function filters out all the nodes whose parent id is
 * listed in expanded node id list.
 *
 * @returns {Array} - filtered array with visible nodes
 */
const visibleRows = ({ rows, expandedNodeIds }) => {
  var isVisible = row => {
    const firstCell = _.first(row);

    if (firstCell.parent === "0" || _.isEmpty(firstCell.parent)) {
      return true;
    }

    return !!_.find(expandedNodeIds, id => id === firstCell.parent);
  };

  return _.filter(rows, isVisible);
};

export const visibleRowsMemoized = _.memoize(
  params => visibleRows(params),
  params => JSON.stringify(params)
);

export const getRethumb = imageUrl => {
  const rethumbUrl = imageUrl
    ? getRethumbUrl({
        width: 144,
        height: 144,
        imageUrl: imageUrl,
        fitIn: true,
      })
    : "";
  return `url("${rethumbUrl}")`;
};

export const getFilterDropdownListMemoized = _.memoize(
  ({ items }) =>
    _.map(items, item => ({
      label: item.name,
      value: item.id,
      key: item.id,
    })),
  params => JSON.stringify(params)
);

export const getFilterSubjectDropdownListMemoized = _.memoize(
  ({ items }) =>
    _.map(
      getArchivedSortedSubjectListMemoized({ subjectList: items }),
      item => ({
        label: item.name,
        value: item.id,
        key: item.id,
        isArchived: _.get(item, "isArchived", false),
      })
    ),
  params => JSON.stringify(params)
);

export const getSubjectGradePairLabel = ({
  grade,
  subject,
  overviewType,

  showSubjectGradePairLabel,
}) => {
  const subjectGradePairLabel = `${_.get(grade, "name", "")} - ${_.get(
    subject,
    "name",
    ""
  )}`;

  return !showSubjectGradePairLabel
    ? _.isEqual(overviewType, "VERTICAL_OVERVIEW")
      ? _.get(grade, "name", "")
      : _.get(subject, "name", "")
    : subjectGradePairLabel;
};

export const getRootNodes = ({ nodes }) => {
  return _.filter(nodes, node => _.isEmpty(node.parent));
};

export const getSubjectGradeId = ({
  data,
  firstGradeId = generateRandomId(),
  firstSubjectId = generateRandomId(),
}) => {
  const subjectId = _.get(data, "subject.id", firstGradeId);
  const gradeId = _.get(data, "grade.id", firstSubjectId);

  return `${subjectId}-${gradeId}`;
};

const getInsightElementKeyByCurriculumType = ({ curriculumType }) => {
  switch (curriculumType) {
    case CURRICULUM_TYPE_MYP:
      return "insightElementSGOFieldsMYP";
    default:
      return "insightElementSO";
  }
};

// maintaining a mapping of the labels for Subject / Subject group overview
// except planner elements as we have another mapping for that already defined
const SO_FIELD_LABEL_MAPPING = {
  centralIdea: "common:centralIdea_label",
  lois: "common:lois_label",
  hours: "unitPlan:unit_duration",
  soi: "common:soi_label",
  content: "common:content_label",
  about: "common:unit_summary",
  transferGoals: "common:transfer_goals_label",
  studentUnderstand: "understandings_label",
  teacherQues: "common:essential_questions_label",
};

const updateTemplateMemoize = getMemoizedLangFunction(params =>
  updateTemplate(params)
);

export const getLabelWithArchivedTextAndDescription = ({
  label,
  isArchived,
  t,
  description,
  isFocused,
  onFocus,
  onBlur,
}) => {
  const labelClass = classNames({
    ["inline"]: true,
  });
  const containerClass = classNames({
    ["block"]: true,
  });
  const styles = {
    archivedText: {
      color: colors.textDisabled,
      marginLeft: "4px",
      display: "inline",
    },
  };
  return (
    <div className={containerClass}>
      <div className={labelClass}>{label}</div>

      {isArchived ? (
        <div style={styles.archivedText}>{`(${t("common:archived")})`}</div>
      ) : null}
      {ACLStore.can("FeatureFlag:EnableDescriptionInSNS") && description && (
        <span style={{ marginLeft: "4px" }}>
          <Tooltip
            tooltip={description}
            overlayInnerStyle={{ maxWidth: "344px" }}
          >
            <InformationOutlined
              size={"xxx-small"}
              variant="subtle"
              style={{
                position: "relative",
                top: "4px",
                outline: isFocused ? "1px solid var(--border-focus)" : "none", //added for accessibility
              }}
              tabIndex={0}
              onFocus={onFocus}
              onBlur={onBlur}
            />
          </Tooltip>
        </span>
      )}
    </div>
  );
};

/**
 * This function returns the mapping of grades and insightFields
 * which in turn used as columns in subject group/subject overview.
 */
export const getGradeInsightFieldsMap = ({
  t,
  curriculumType,
  gradeTemplatesMap,
  unitPlanTemplates,
  plannerElementSets,
  fieldListsV2,
}) => {
  const gradeInsightFieldsMap = {};
  const insightColumns = [];
  let fieldUids = [];

  _.forEach(gradeTemplatesMap, grade => {
    const insightFields = {};

    const gradeTemplateIds = _.map(_.get(grade, "plannerTemplates", []), "id");
    const gradeTemplates = _.filter(unitPlanTemplates, template =>
      _.includes(gradeTemplateIds, template.id)
    );

    const insightElementKey = getInsightElementKeyByCurriculumType({
      curriculumType,
    });

    // reduce gradeTemplates to a single array with unique fieldUIDs from each template's insightElements
    const insightElements = _.uniq(
      _.flatten(
        _.map(gradeTemplates, template =>
          _.get(template, ["body", insightElementKey], [])
        )
      )
    );

    fieldUids.push(...insightElements);

    // need to show contexts and focus areas together
    if (_.includes(insightElements, "globalContextMYP")) {
      insightElements.push("explorationDevelopMYP");
    }

    _.forEach(insightElements, fieldKey => {
      insightFields[fieldKey] = {};

      _.forEach(unitPlanTemplates, unitPlanTemplate => {
        const updatedUnitPlanTemplate = _.get(
          updateTemplateMemoize({
            template: unitPlanTemplate,
            curriculumType,
            shouldUpdateViewTypeInSinglePage: false,
          }),
          "body",
          {}
        );

        const field_list = _.get(updatedUnitPlanTemplate, "field_list", {});
        const fieldObj = field_list[fieldKey];

        const sgoConfig = _.get(unitPlanTemplate, "body.sgoConfig", {});
        const {
          dependentFields,
          isYesNoField = false,
          columnLabel,
          useStepLabel = false,
        } = sgoConfig[fieldKey] || {};

        if (fieldObj) {
          const fieldObjConfig = _.get(fieldObj, "config", {});

          // update labels
          const plannerElementType = _.get(
            fieldObjConfig,
            "plannerElementConfig.type",
            ""
          );

          // for UbD: take planner element label from the sets
          // this is already localised
          const ubdPlannerElementLabel = _.isEqual(
            curriculumType,
            CURRICULUM_TYPE_UBD
          )
            ? plannerElementSets[plannerElementType]?.label
            : null;

          let plannerElementLabel =
            PLANNER_ELEMENT_LABEL_MAPPING[plannerElementType];

          let isLocalised = false;

          if (ACLStore.can("FeatureFlag:GetLableFromPlannerElementSet")) {
            const labelFromSet = _.get(plannerElementSets, [
              plannerElementType,
              "label",
            ]);

            if (labelFromSet) {
              plannerElementLabel = labelFromSet;
              isLocalised = true;
            }
          }

          let label;

          if (ubdPlannerElementLabel) {
            label = ubdPlannerElementLabel;
          } else if (!_.isEmpty(plannerElementLabel)) {
            label = isLocalised ? plannerElementLabel : t(plannerElementLabel);
          } else {
            const fieldLabel = _.get(fieldObjConfig, "label", "");

            label = !_.isEmpty(fieldLabel)
              ? fieldLabel
              : t(SO_FIELD_LABEL_MAPPING[fieldKey]);
          }

          // need a different label for global contexts
          if (_.isEqual(fieldKey, "globalContextMYP")) {
            label = t("common:global_context_and_focus_areas_label");
          }

          if (!_.isEmpty(label) || ubdPlannerElementLabel) {
            fieldObj["config"] = {
              ...fieldObjConfig,
              label,
            };
          }

          if (useStepLabel) {
            const stepList = _.get(updatedUnitPlanTemplate, "step_list", {});
            const step = _.find(stepList, step => {
              return _.includes(step.fields, fieldKey);
            });

            if (step && !_.isEmpty(step.label)) {
              label = step.label;
            }
          }

          if (!_.isEmpty(fieldObj))
            insightFields[fieldKey][unitPlanTemplate?.id] = fieldObj;

          const existingColumn = _.find(insightColumns, { id: fieldKey });

          if (!existingColumn) {
            insightColumns.push({
              label,
              id: fieldKey,
              // not shown in configure columns options, only for merging with contexts later
              ...(_.isEqual(fieldKey, "explorationDevelopMYP")
                ? {
                    isHidden: true,
                    mergedWithField: "globalContextMYP",
                    showLabel: true,
                  }
                : {}),
            });
          } else {
            if (isYesNoField && !_.get(existingColumn, "isYesNoField", false)) {
              existingColumn.isYesNoField = true;
            }
          }
        }

        const fieldListV2 = _.get(fieldListsV2, unitPlanTemplate.id, {});
        const isPortedField = _.has(fieldListV2, fieldKey);

        if (isPortedField) {
          const fieldObjV2 = fieldListV2[fieldKey];

          if (!_.isEmpty(fieldObjV2)) {
            const label = !_.isEmpty(columnLabel)
              ? columnLabel
              : _.get(fieldObjV2, "label.value", "");

            const existingColumn = _.find(insightColumns, { id: fieldKey });

            if (!existingColumn) {
              insightColumns.push({
                id: fieldKey,
                label,
                isYesNoField,
              });
            } else {
              if (
                isYesNoField &&
                !_.get(existingColumn, "isYesNoField", false)
              ) {
                existingColumn.isYesNoField = true;
              }
            }
          }
        }

        if (fieldObj || isPortedField) {
          _.forEach(dependentFields, field => {
            if (!_.find(insightColumns, { id: field })) {
              insightColumns.push({
                id: field,
                label: "",
                isHidden: true,
                showLabel: false,
                mergedWithField: fieldKey,
                isYesNoField,
              });

              insightFields[field] = {
                [unitPlanTemplate?.id]: fieldObj,
              };
            }
          });
        }
      });
    });

    gradeInsightFieldsMap[grade.id] = insightFields;
  });

  fieldUids = _.uniq(fieldUids);

  return {
    insightColumns,
    gradeInsightFieldsMap,
    templateFieldUids: fieldUids,
  };
};

export const getSelectedSubjectIds = ({
  subjectId,
  currentPlanningInsight,
  currentTableType,
  subjects,
  isCustomDataset,
}) => {
  if (isCustomDataset) {
    return getActiveSubjectIdsMemoized({ subjects });
  }

  const selectedSubjectId = subjectId ? subjectId : _.first(subjects)?.id;
  switch (currentPlanningInsight) {
    case "gc":
    case "sgo":
    case "poi":
    case "lpas":
    case "atl": {
      return getActiveSubjectIdsMemoized({ subjects });
    }
    case "krc": {
      if (currentTableType !== "MYP_RELATED_CONCEPT") {
        return getActiveSubjectIdsMemoized({ subjects });
      }
      return selectedSubjectId ? [selectedSubjectId] : [];
    }

    default: {
      return selectedSubjectId ? [selectedSubjectId] : [];
    }
  }
};
export const getAcademicYearFromId = ({ academicYearId }) => {
  const { startDate, endDate } = getAcademicYearDetailsFromCahce(
    academicYearId
  );
  return getYearDurationFormat({ startDate, endDate });
};

export const getAcademicYearId = ({
  academicYearId,
  organizationId,
  academicYearSelected,
}) => {
  if (!_.isEmpty(academicYearId)) {
    return academicYearId;
  }

  const currentAcademicYear = getCurrentAcademicYear({
    organizationId,
  });
  const currentAcademicYearId = _.get(currentAcademicYear, "id", null);
  return _.get(academicYearSelected, "id", currentAcademicYearId);
};

export const getPlanningInsightsProps = ({ sourceType }) => {
  let sourceTypeBasedConfig = {};

  switch (sourceType) {
    case "SNP":
      {
        sourceTypeBasedConfig = {
          planningInsightProps: {
            showBreadCrumbs: false,
            showBackButton: false,
            showInsightsDropdown: false,
            isViewOnly: true,
          },
        };
      }
      break;
    default:
      sourceTypeBasedConfig = {
        planningInsightProps: {
          showBreadCrumbs: true,
          showBackButton: true,
          showInsightsDropdown: true,

          isViewOnly: false,
        },
      };
  }

  return sourceTypeBasedConfig;
};

const DEFAULT_SOURCE_TYPE_CONFIG = {
  insightProps: {
    showPrintButton: true,
  },
};

export const getPypPlanningInsightProps = ({ sourceType }) => {
  let sourceTypeBasedConfig = {};

  switch (sourceType) {
    case "SNP":
      {
        sourceTypeBasedConfig = {
          poiProps: {
            showBackButton: false,
            showBreadCrumbs: false,
            showYearSwitcher: false,
            showInsightSwitcher: false,
            showPrintButton: false,
            showYearlyDuration: true,
          },
          insightProps: {
            showBackButton: false,
            showBreadCrumbs: false,
            showInsightSwitcher: false,
            showPrintButton: false,
            isViewOnly: true,
          },
        };
      }
      break;
    case "timelineview": {
      sourceTypeBasedConfig = {
        insightProps: {
          showPrintButton: false,
        },
      };
      break;
    }
    default: {
      // done to avoid rerender due to reference change
      sourceTypeBasedConfig = DEFAULT_SOURCE_TYPE_CONFIG;
    }
  }

  return sourceTypeBasedConfig;
};

export const getPlannerElementSetInsightLabel = ({
  elementTypes,
  plannerElementSets,
}) => {
  let combinedLabel = "";

  _.forEach(elementTypes, (elementType, index) => {
    plannerElementSets[elementType]?.label;

    if (_.size(elementTypes) > 1 && index == _.size(elementTypes) - 1) {
      combinedLabel += `& ${plannerElementSets[elementType]?.label}`;
    } else {
      if (_.size(elementTypes) < 3) {
        combinedLabel += `${plannerElementSets[elementType]?.label} `;
      } else {
        combinedLabel += `${plannerElementSets[elementType]?.label}, `;
      }
    }
  });

  return combinedLabel;
};

export const getDynamicPlannerElementLabelStatus = ({ item }) => {
  return (
    (item?.dynamicPlannerElementLabelPerm &&
      ACLStore.can(item.dynamicPlannerElementLabelPerm)) ||
    item.isDynamicPlannerElementLabel
  );
};

export const getInsightsLabel = ({
  item,
  plannerElementSets,
  t,
  appendInsightsLabel = true,
}) => {
  if (getDynamicPlannerElementLabelStatus({ item })) {
    const elementType = item.elementTypes;
    const label = plannerElementSets[elementType]?.label;
    return appendInsightsLabel
      ? t("planningInsights:insights_with_label", { label })
      : label;
  }

  // custom datasets label is user defined and can not be localised
  if (_.get(item, "isCustomDataset", false)) return item.label;

  return t(item.dropdownLabel ?? item.label);
};

export const getUnitTypeOptions = ({ t, curriculumType }) => {
  switch (curriculumType) {
    case CURRICULUM_TYPE_MYP:
      return [
        { label: t("common:all"), key: "ALL" },
        {
          label: t("common:subject_specific_units"),
          key: MYP_SUBJECT_SPECIFIC,
        },
        {
          label: t("common:interdisciplinary_units"),
          key: MYP_INTERDISCIPLINARY,
        },
      ];

    default:
      return [];
  }
};

export const getUnitTypeOptionsMemoized = _.memoize(
  params => getUnitTypeOptions(params),
  params => JSON.stringify(params)
);

const getPlannerElementLabel = ({
  item,
  plannerElementSets,
  t,
  appendView = false,
}) => {
  const isCustomDataset = _.get(item, "isCustomDataset", false);
  if (isCustomDataset) {
    return item.label;
  }

  const isDynamicPlannerElementLabel = getDynamicPlannerElementLabelStatus({
    item,
  });

  if (!isDynamicPlannerElementLabel) {
    return t(_.get(item, "label", ""));
  }

  let plannerElementLabel = plannerElementSets[item.elementTypes]?.label;
  if (appendView) {
    plannerElementLabel = `${plannerElementLabel} ${_.toLower(
      t("common:view")
    )}`;
  }

  return plannerElementLabel;
};

export const getPlannerElementLabelMemoized = getMemoizedLangFunction(
  getPlannerElementLabel
);

const getGroupedSubjectList = ({ subjects }) => {
  const groupedBySubjectGroups = _.reduce(
    getArchivedSortedSubjectListMemoized({ subjectList: subjects }),
    (result, item) => {
      const subjectGroup = item.subjectGroup;
      if (!_.find(result, { value: subjectGroup?.id })) {
        if (subjectGroup) {
          result.push({
            label: `All in ${subjectGroup?.name}`,
            value: subjectGroup?.id,
            displaySequence: subjectGroup?.displaySequence,
            options: [
              {
                label: item?.name,
                value: item?.id,
                isArchived: item?.isArchived,
              },
            ],
          });
        }
      } else {
        const index = _.findIndex(result, { value: subjectGroup?.id });

        result = update(result, {
          [index]: {
            options: {
              $push: [
                {
                  label: item?.name,
                  value: item?.id,
                  isArchived: item?.isArchived,
                },
              ],
            },
          },
        });
      }

      return result;
    },
    []
  );

  return _.concat(
    {
      value: `divider`,
      isDivider: true,
    },

    _.sortBy(groupedBySubjectGroups, ["displaySequence"])
  );
};

export const getGroupedSubjectListMemoized = _.memoize(
  params => getGroupedSubjectList(params),
  props => JSON.stringify(props)
);

const getGradeSubjectPair = ({ grades, subjects, selectedSubjectIds = [] }) => {
  return _.reduce(
    grades,
    (result, grade) => {
      const filteredSubjects = _.filter(subjects, subject => {
        return (
          _.includes(
            _.map(_.get(grade, "subjects", []), item => item?.id),
            subject?.id
          ) && _.includes(selectedSubjectIds, subject?.id)
        );
      });

      _.forEach(filteredSubjects, subject => {
        result.push({
          subject,
          grade,
        });
      });

      return result;
    },
    []
  );
};

export const getGradeSubjectPairMemoized = _.memoize(
  params => getGradeSubjectPair(params),
  props => JSON.stringify(props)
);

// we should only consider leaf options for label
// when subjects are grouped by subject group
const getGroupedSubjectsLabelOptions = ({ subjectListOptions }) => {
  return _.reduce(
    subjectListOptions,
    (result, subjectGroup) => {
      if (!_.isEmpty(subjectGroup.options)) {
        result = [...result, ...subjectGroup.options];
      }
      return result;
    },
    []
  );
};

export const getGroupedSubjectsLabelOptionsMemoized = _.memoize(
  params => getGroupedSubjectsLabelOptions(params),
  props => JSON.stringify(props)
);

const getSubjectListOptions = ({ subjects, curriculumType }) => {
  const curriculumHasSubjectGroups = _.get(
    CURRICULUM_CONFIGURATION,
    [curriculumType, "hasSubjectGroups"],
    false
  );

  if (!curriculumHasSubjectGroups) {
    const subjectList = _.map(subjects, subject => {
      return {
        label: subject.name,
        value: subject.id,
        isArchived: _.get(subject, "isArchived", false),
      };
    });

    return { subjectList, subjectListOptionsForLabel: subjectList };
  }

  const subjectList = getGroupedSubjectListMemoized({ subjects });

  return {
    subjectList,
    subjectListOptionsForLabel: getGroupedSubjectsLabelOptionsMemoized({
      subjectListOptions: subjectList,
    }),
  };
};

export const getSubjectListOptionsMemoized = _.memoize(
  params => getSubjectListOptions(params),
  props => JSON.stringify(props)
);

export const getSubjectListOptionsWithKeys = ({ subjects, curriculumType }) => {
  const {
    subjectList,
    subjectListOptionsForLabel,
  } = getSubjectListOptionsMemoized({
    subjects,
    curriculumType,
  });

  const subjectListWithKeys = _.map(subjectList, item => ({
    ...item,
    key: item.value,
  }));

  const subjectListOptionsForLabelWithKeys = _.map(
    subjectListOptionsForLabel,
    item => ({
      ...item,
      key: item.value,
    })
  );

  return {
    subjectList: subjectListWithKeys,
    subjectListOptionsForLabel: subjectListOptionsForLabelWithKeys,
  };
};

const getActiveSubjectIds = ({ subjects }) => {
  return _.map(_.filter(subjects, ["isArchived", false]), "id");
};

export const getActiveSubjectIdsMemoized = _.memoize(
  params => getActiveSubjectIds(params),
  props => JSON.stringify(props)
);

const getExcludeUnitTooltip = ({
  t,
  isCurriculumPYP,
  isExcludingFromProgrammeOverview,
  grade,
}) => {
  if (!isCurriculumPYP) {
    return t("unitPlan:exclude_unit");
  }

  if (isExcludingFromProgrammeOverview) {
    return t("unitPlan:exclude_unit_from_grade", {
      gradeName: _.get(grade, "name", ""),
    });
  }

  return t("unitPlan:exclude_unit_from_all_views");
};

export const getExcludeUnitTooltipMemoized = _.memoize(
  params => getExcludeUnitTooltip(params),
  props => JSON.stringify(props)
);

const getFilteredOverviewItems = ({ items, subjectType, subjectGroupType }) => {
  return _.filter(items, item => {
    let shouldSkip = false;

    if (_.has(item, "skipForSubjectGroup")) {
      shouldSkip = _.includes(item.skipForSubjectGroup, subjectGroupType);
    }

    if (!shouldSkip && _.has(item, "skipForSubjectType")) {
      shouldSkip = _.includes(item.skipForSubjectType, subjectType);
    }

    return !shouldSkip;
  });
};

export const getFilteredOverviewItemsMemoized = _.memoize(
  params => getFilteredOverviewItems(params),
  props => JSON.stringify(props)
);

const getCPPlannerElementTypes = ({
  plannerElementTypes,
  insightType,
  isAdminTabActive = false,
}) => {
  const isCPCoreEnabled = ACLStore.can("FeatureFlag:EnableCPCore");
  if (!isCPCoreEnabled) return plannerElementTypes;

  if (isAdminTabActive && _.isEqual(insightType, "horizontal-connections")) {
    if (!_.includes(plannerElementTypes, "DP_CP_FRAMEWORK_INTERSECTIONS")) {
      return [...plannerElementTypes, "DP_CP_FRAMEWORK_INTERSECTIONS"];
    }

    return plannerElementTypes;
  }

  switch (insightType) {
    case "connections": {
      return [
        "DP_ATL",
        "DP_METACOGNITION_APPROACH",
        "DP_DIFFERENTIATION_APPROACH",
        "DP_CP_FRAMEWORK_INTERSECTIONS",
      ];
    }

    case "horizontal-connections": {
      return [
        "DP_ATL",
        "DP_METACOGNITION_APPROACH",
        "DP_CP_FRAMEWORK_INTERSECTIONS",
      ];
    }

    default:
      return plannerElementTypes;
  }
};

export const getCPPlannerElementTypesMemoized = _.memoize(
  params => getCPPlannerElementTypes(params),
  props => JSON.stringify(props)
);

export const getGradeAndSubjectFromSidebarElement = selectedSideBarItem => {
  const isSingleTableInsights = isSingleTableInsightsEnabled();

  if (isSingleTableInsights) {
    return {
      selectedSubjectId: null,
      selectedGradeId: selectedSideBarItem,
    };
  }

  let selectedSubjectId;
  let selectedGradeId;
  if (selectedSideBarItem) {
    const selectedSideBarSubjectGrade = selectedSideBarItem?.split("-");
    selectedSubjectId = selectedSideBarSubjectGrade?.[0];
    selectedGradeId = selectedSideBarSubjectGrade?.[1];
  }
  return { selectedSubjectId, selectedGradeId };
};

export const getSGOSubjectAndGradeIds = ({
  selectedSidebarItem,
  verticalOverviewFilters,
  isPaginatedSGO,
  isSingleTableInsights,
  allSubjectIds,
}) => {
  // single table insights takes the highest precedence
  // take values from filter dropdowns
  if (isSingleTableInsights) {
    const {
      selectedStandardsSubject: selectedFilterSubject,
    } = verticalOverviewFilters;

    const gradeIds = selectedSidebarItem ? [selectedSidebarItem] : [];

    return {
      subjectIds: [selectedFilterSubject],
      // for non paginated sgo, we will fetch all grades always
      gradeIds: isPaginatedSGO ? gradeIds : [],
    };
  }

  // if paginated, use sidebar item to determine grades and subjects
  if (isPaginatedSGO) {
    const {
      selectedSubjectId: selectedSidebarSubjectId,
      selectedGradeId: selectedSidebarGradeId,
    } = getGradeAndSubjectFromSidebarElement(selectedSidebarItem);

    return {
      subjectIds: [selectedSidebarSubjectId],
      gradeIds: [selectedSidebarGradeId],
    };
  }

  // otherwise, pass all subjects in variables
  return {
    subjectIds: allSubjectIds,
    gradeIds: [],
  };
};

export const handleVerticalNavigation = e => {
  const { key } = e;
  const currentCell = e.currentTarget;
  switch (key) {
    case "ArrowDown": {
      const nextRow = currentCell.nextElementSibling;
      if (nextRow) {
        nextRow.focus();
      }
      break;
    }
    case "ArrowUp": {
      const prevRow = currentCell.previousElementSibling;
      if (prevRow) {
        prevRow.focus();
      }
      break;
    }
    default:
      break;
  }
};

export const handleHorizontalNavigation = e => {
  const { key } = e;
  const currentCell = e.currentTarget;

  switch (key) {
    case "ArrowRight":
      if (currentCell.nextElementSibling) {
        currentCell.setAttribute("tabindex", -1);
        currentCell.nextElementSibling.setAttribute("tabindex", 0);
        currentCell.nextElementSibling.focus();
      }
      break;

    case "ArrowLeft":
      if (currentCell.previousElementSibling) {
        currentCell.setAttribute("tabindex", -1);
        currentCell.previousElementSibling.setAttribute("tabindex", 0);
        currentCell.previousElementSibling.focus();
      }
      break;

    default:
      break;
  }
};

const _getCustomPlannerElementSetsQueryVariables = ({
  currentCurriculumProgram,
}) => {
  if (_.isEmpty(currentCurriculumProgram)) {
    return null;
  }

  const {
    id,
    nodeInterfaceType: curriculumNodeInterfaceType,
  } = currentCurriculumProgram;

  const filters = {
    isIncludedInInsights: true,
  };

  return {
    id,
    curriculumNodeInterfaceType,
    category: ["CUSTOM"],
    // discrepancy between filter types from BE
    filters,
    _filters: filters,
  };
};

export const getCustomPlannerElementSetsQueryVariables = getMemoizedFunction(
  _getCustomPlannerElementSetsQueryVariables
);

const _getTemplateIdsQueryVariables = ({
  organizationId,
  curriculumProgramId,
}) => {
  return {
    id: organizationId,
    filters: {
      templateType: "UNIT_PLAN",
      curriculumProgramIds: curriculumProgramId ? [curriculumProgramId] : [],
    },
  };
};

export const getTemplateIdsQueryVariables = getMemoizedFunction(
  _getTemplateIdsQueryVariables
);
